import { useEffect } from 'react';
import {
  useSelectedOrganizationId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';

import request from 'utils/request';
import type { FunctionalTest } from './types';


const TOKEN_INVALID = 'Token is not valid';

export const FUNCTIONAL_TESTS_QUERY_KEY = ['functionalTesting'] as const;

export interface FunctionalTestsParams {
  page: number;
  pageSize: number;
  boardId?: string;
  deviceId?: string;
  boardType?: string;
  passedOnly?: boolean;
  failedOnly?: boolean;
  location?: string;
  dateFrom?: string;
  dateTo?: string;
  countUnique?: boolean;
}

export interface FunctionalTestsResult {
  values: FunctionalTest[];
  total: number;
}

type FunctionalTestsResponse = {
  success?: boolean;
  message?: string;
  data?: FunctionalTest[];
  total?: number;
  page?: number;
  pageSize?: number;
};

function buildQueryString(params: FunctionalTestsParams): string {
  const search = new URLSearchParams({
    page: params.page.toString(),
    pageSize: params.pageSize.toString(),
  });
  if (params.boardId) search.append('boardId', params.boardId);
  if (params.deviceId) search.append('deviceId', params.deviceId);
  if (params.boardType) search.append('boardType', params.boardType);
  if (params.passedOnly) search.append('passedOnly', 'true');
  if (params.failedOnly) search.append('failedOnly', 'true');
  if (params.location) search.append('location', params.location);
  if (params.dateFrom) search.append('dateFrom', params.dateFrom);
  if (params.dateTo) search.append('dateTo', params.dateTo);
  if (params.countUnique) search.append('countUnique', 'true');
  return search.toString();
}

async function fetchFunctionalTests(
  params: FunctionalTestsParams,
  authToken: string
): Promise<FunctionalTestsResult> {
  const result = await request<FunctionalTestsResponse>(
    `/api/devices/hardwareTests/all?${buildQueryString(params)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', authorization: authToken },
    }
  );
  if (result?.success === false) {
    if (result.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
    throw new Error(result.message || 'Failed to fetch tests');
  }
  return { values: result?.data ?? [], total: result?.total ?? 0 };
}

export function functionalTestsQueryOptions(
  params: FunctionalTestsParams,
  authToken: string,
  organizationId: number
) {
  return queryOptions({
    queryKey: [...FUNCTIONAL_TESTS_QUERY_KEY, organizationId, params],
    queryFn: () => fetchFunctionalTests(params, authToken),
    placeholderData: keepPreviousData,
  });
}

function useLogoutOnInvalidToken(error: unknown) {
  useEffect(() => {
    if (error instanceof Error && error.message === TOKEN_INVALID) {
      logout();
    }
  }, [error]);
}

export function useFunctionalTestsQuery(params: FunctionalTestsParams) {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();
  const query = useQuery(
    functionalTestsQueryOptions(params, authToken, organizationId)
  );
  useLogoutOnInvalidToken(query.error);
  return query;
}
